import { useCallback, useState, useEffect } from 'react';
import { profileTypes } from '../../../config/constants/userConstants';
import { UsersService } from '../../../services/usersService';
import { getSchedulingBlocks } from '../../../utility/selfSchedulingUtils';
import { useNotifications } from '../../../hooks/useNotifications';

const useUser = ({ userId, profileId }) => {
  const [userData, setUserData] = useState({
    schedulingTimes: getSchedulingBlocks([]),
    profile: {
      specialities: [],
    },
  });
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [countUsers, setCountUsers] = useState();
  const { showErrorNotification } = useNotifications();

  const checkError = (message) => {
    if (message == null) {
      return false;
    }
    const errorMessage = {
      [UsersService.USERNAME_ALREADY_EXISTS]: 'usernameAlreadyExists',
      [UsersService.EMAIL_ALREADY_EXISTS]: 'emailAlreadyExists',
    }[message];
    if (errorMessage == null) {
      return false;
    }
    showErrorNotification({
      title: 'error',
      description: errorMessage,
    });
    return true;
  };

  const getUserData = useCallback(async () => {
    if (userId == null || !userId?.length) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await UsersService.getById(userId);
      const newUserData = {
        ...data.user,
        schedulingTimes: getSchedulingBlocks(data.schedulingTimes ?? []),
        servicesTypesAssigned: data.servicesTypesAssigned ?? [],
        scheduleLocks: data.scheduleLocks ?? [],
      };
      setUserData(newUserData);
    } catch (error) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const getCountUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const { profileCounts } = await UsersService.getUsers(profileId);
      setCountUsers({
        data: profileCounts,
      });
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  });

  const getProfileData = (data) => {
    return {
      identification: data.identification,
      name: data.name,
      idProfile: data.idProfile,
      cellular: data.cellular,
      phone: data.phone,
      idsHeadquarters: data.idsHeadquarters,
      // interested
      companies: data.companies,
      typePetition: data.typePetition,
      idTypePetition: data.idTypePetition,
      canCreatePetitions: data.canCreatePetitions,
      // technician
      specialities: data.specialities,
      idZones: data.idZones,
      confirmationChangeStatus: data.confirmationChangeStatus,
      canCreateServices: data.canCreateServices,
      canStartSimultaneousServices: data.canStartSimultaneousServices,
      canViewServicesHistory: data.canViewServicesHistory,
      allowToViewAllUsersHistory: data.allowToViewAllUsersHistory,
      allowToSubmitServiceTags: data.allowToSubmitServiceTags,
      enableAvailabilitySchedule: data.enableAvailabilitySchedule,
      schedulingTimes: data.schedulingTimes,
      scheduleLocks: data.scheduleLocks,
      useExplicitServiceTypes: data.useExplicitServiceTypes,
      explicitServiceTypes: data.explicitServiceTypes,

      // operator
      administrateServices: data.administrateServices,
      administrateClients: data.administrateClients,
      administrateAdvancedAvailability: data.administrateAdvancedAvailability,
      adminAllUsers:
        data.adminAllUsers || (data.idProfile === profileTypes.OPERATOR.id && !data.resourcesEnabledToAdmin?.length),
      resourcesEnabledToAdmin: data.resourcesEnabledToAdmin,
      administrateInventory: data.administrateInventory,
      seeAllServices: data.seeAllServices,
      isAllowedToViewAllAuthors: data.isAllowedToViewAllAuthors,
      serviceTypesEnabledToView: data.serviceTypesEnabledToView,
      canChangeServiceStatus: data.canChangeServiceStatus,
      canCancelServices: data.canCancelServices,
      canCreateIncidentNotes: data.canCreateIncidentNotes,
      canDeleteIncidentNotes: data.canDeleteIncidentNotes,
      canViewCustomers: data.canViewCustomers,
      canEditCustomers: data.canEditCustomers,
      canViewCalendar: data.canViewCalendar,
      canViewDashboard: data.canViewDashboard,
      canViewServicesSummary: data.canViewServicesSummary,
      canEditServicesSummary: data.canEditServicesSummary,
      canViewIncidentNotes: data.canViewIncidentNotes,
      canViewAssets: data.canViewAssets,
      canEditAssets: data.canEditAssets,
      canViewReviewForm: data.canViewReviewForm,
      canEditReviewForm: data.canEditReviewForm,
      canViewResourceTracking: data.canViewResourceTracking,
      canEditResourceTracking: data.canEditResourceTracking,
      canViewFinalCustomers: data.canViewFinalCustomers,
      canEditFinalCustomers: data.canEditFinalCustomers,
      canManageCustomers: data.canManageCustomers,
      isMandatoryGPS: data.isMandatoryGPS,
      isAllowedToViewAllCompanies: data.isAllowedToViewAllCompanies,
      enableSupportChat: data.enableSupportChat,
      assignableServiceProfiles: data.assignableServiceProfiles,
      canViewSurveys: data.canViewSurveys,
      canCreateCreditNotes: data.canCreateCreditNotes,
      administrateQuickAnswers: data.administrateQuickAnswers,
      canViewBillingSettings: data.canViewBillingSettings,
      canCreateInvoices: data.canCreateInvoices,
      canCreateAdjustmentNotes: data.canCreateAdjustmentNotes,
      canConfigureInventory: data.canConfigureInventory,
      administrateWorkflows: data.administrateWorkflows,
      seeAllWorkflows: data.seeAllWorkflows,
      canViewCustomLists: data.canViewCustomLists,
      ...(data.idProfile === profileTypes.SUPERVISOR.id && { canManageUsers: data.canManageUsers }),
    };
  };

  const createUser = async (data) => {
    setIsLoading(true);
    try {
      const newUserData = {
        username: data.username,
        email: data.email,
        newPassword: data.password,
        profile: getProfileData(data),
      };
      await UsersService.createUser(newUserData);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = async (data) => {
    setIsLoading(true);
    try {
      const newUserData = {
        username: data.username,
        email: data.email,
        profile: getProfileData(data),
      };
      await UsersService.updateUser(userId, newUserData);
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async (data) => {
    const idUser = data.userId ?? userId;

    setIsLoading(true);
    try {
      const newUserData = {
        newPassword: data.password,
      };
      await UsersService.updatePasswordUser(idUser, newUserData);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getUserData();
  }, [getUserData]);

  return {
    userData,
    createUser,
    updateUser,
    hasError,
    isLoading,
    getCountUsers,
    countUsers,
    updatePassword,
    checkError,
  };
};

export default useUser;
